import { CalendarIcon } from '@chakra-ui/icons'
import { Center, HStack, Image, Text, TextProps, VStack } from '@chakra-ui/react'
import {
  AssessmentInvoiceSnippet,
  colors,
  getDateString,
  Invoice,
  PracticeInvoice,
} from '@hb/shared'
import { PropsWithChildren } from 'react'
import { ActionLog } from '../ActionLog'

const InvoiceStatusIcon = ({
  status,
  manuallyAddedBy,
  sentBy,
  sentOn,
}: {
  status: Invoice['status']
  manuallyAddedBy?: string
  sentOn?: string | null
  sentBy?: string | null
}) => {
  if (status === 'scheduled') {
    if (manuallyAddedBy) {
      return <Image src="/images/person.svg" width="20px" opacity={0.6} />
    }
    return <CalendarIcon color="gray.500" width={3.5} height={3.5} />
  }

  if (status === 'draft') {
    return <Image src="/images/draft.svg" height="20px" opacity={0.6} />
  }

  if (status === 'sent') {
    return <ActionLog group="admin" on={sentOn} by={sentBy} action="Sent" />
  }
  if (status === 'pending-approval') {
    return <Image opacity={0.7} src="/images/person.svg" height="20px" />
  }
  return (
    <Center
      fontSize="xs"
      // border={`1px solid ${statusColor}`}
      bg={status === 'paid' ? colors.green.hex : 'transparent'}
      border={status === 'paid' ? 'none' : '1px solid #777'}
      color={status === 'paid' ? 'white' : '#777'}
      width="18px"
      mr={0.5}
      borderRadius={4}
      height="18px"
      fontWeight={600}>
      $
    </Center>
  )
}

const StatusText = ({ children, color }: PropsWithChildren<{ color?: TextProps['color'] }>) => (
  <Text fontFamily="Open Sans" fontSize="0.9rem" color={color || 'gray.600'} lineHeight={1}>
    {children}
  </Text>
)
const ScheduledForText = ({ scheduledFor }: { scheduledFor: string | null }) => {
  if (!scheduledFor) {
    return null
  }
  return <StatusText>Scheduled for {getDateString(scheduledFor, 'short')}</StatusText>
}

export const InvoiceStatusView = ({
  invoice,
}: {
  invoice: PracticeInvoice | AssessmentInvoiceSnippet
}) => {
  const status =
    (invoice as PracticeInvoice).status ?? (invoice as AssessmentInvoiceSnippet).invoiceStatus
  const { scheduledFor } = invoice
  const paidOn =
    (invoice as PracticeInvoice).paidOn ?? (invoice as AssessmentInvoiceSnippet).payment?.paidOn

  const { manuallyAddedBy } = (invoice as AssessmentInvoiceSnippet) ?? {}
  if (status === 'scheduled') {
    return (
      <HStack p={1} spacing={2}>
        <InvoiceStatusIcon
          sentBy={invoice.sentBy}
          sentOn={invoice.sentOn}
          status={status}
          manuallyAddedBy={manuallyAddedBy ?? undefined}
        />
        <ScheduledForText scheduledFor={scheduledFor} />
      </HStack>
    )
  }

  if (status === 'paid') {
    return (
      <HStack spacing={1}>
        <InvoiceStatusIcon status={status} />
        <StatusText>
          Paid on {paidOn ? getDateString(paidOn, 'short') : 'Error: no paid on date'}
        </StatusText>
      </HStack>
    )
  }
  if (status === 'sent') {
    return (
      <HStack py={1}>
        <ActionLog
          style={{ fontSize: '1rem' }}
          on={invoice.sentOn}
          group="admin"
          by={invoice.sentBy}
          action="Sent"
        />
      </HStack>
    )
  }
  if (status === 'draft') {
    return (
      <HStack spacing={1}>
        <InvoiceStatusIcon status={status} />
        <StatusText>Draft</StatusText>
      </HStack>
    )
  }
  if (status === 'pending-approval') {
    return (
      <VStack align="flex-start" spacing={0}>
        <HStack spacing={1}>
          <InvoiceStatusIcon status={status} />
          <StatusText>Pending Approval</StatusText>
        </HStack>
        <ScheduledForText scheduledFor={scheduledFor} />
      </VStack>
    )
  }
  return (
    <HStack>
      <InvoiceStatusIcon status={status} />
      <StatusText>Unknown Status</StatusText>
    </HStack>
  )
}
